import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, EmptyState, ErrorState, Field, Input, Modal, Textarea } from '@bb/ui';
import { ApiError, type AdminApi, type TemplateChannel, type TemplateContent, type TemplateInstance, type TemplateIssue, type TemplateSummary } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { TEMPLATE_CHANNEL_LABELS } from './templateContent.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const issuesOf = (error: unknown): TemplateIssue[] => (error instanceof ApiError && Array.isArray(error.problem.issues) ? (error.problem.issues as TemplateIssue[]) : []);
const useApi = () => (useRouter().options.context as { api: AdminApi }).api;

interface PickerProps {
  environmentId: string;
  channel: TemplateChannel;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A copy of the chosen version's content and its source. */
  onInsert: (instance: TemplateInstance) => void;
}

/**
 * Choose a template of one channel when composing a message (campaign variant, Canvas Message step): archived templates
 * are not offered; inserting copies the current version, so later versions do not change the message.
 */
export function TemplatePickerDialog({ environmentId, channel, open, onOpenChange, onInsert }: PickerProps) {
  const api = useApi();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TemplateSummary | null>(null);
  const list = useQuery({ queryKey: ['templates', environmentId, 'picker', channel, query], queryFn: () => api.listTemplates(environmentId, { channel, query }), enabled: open });
  const insert = useMutation({
    mutationFn: (t: TemplateSummary) => api.instantiateTemplate(environmentId, t.id),
    onSuccess: (instance) => {
      onInsert(instance);
      onOpenChange(false);
    },
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Insert a ${TEMPLATE_CHANNEL_LABELS[channel].toLowerCase()} template`}
      description="The message gets a copy of the template: editing the template later does not change it."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!selected || insert.isPending} onClick={() => selected && insert.mutate(selected)}>
            Insert template
          </Button>
        </>
      }
    >
      <label>
        Search <input aria-label="Search templates" value={query} onChange={(e) => setQuery(e.target.value)} />
      </label>
      {list.isPending ? (
        <p aria-busy="true">Loading templates…</p>
      ) : list.isError ? (
        <ErrorState title="Templates could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title="No templates" description="Save a message as a template, or create one in Content → Templates." />
      ) : (
        <ul className="template-list" aria-label="Templates">
          {list.data.items.map((t) => (
            <li key={t.id}>
              <button type="button" className="template-card" aria-pressed={selected?.id === t.id} onClick={() => setSelected(t)}>
                <strong>{t.name}</strong>
                <span className="hint">
                  v{t.currentVersion}
                  {t.tags.length ? ` · ${t.tags.join(', ')}` : ''}
                  {t.description ? ` · ${t.description}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {insert.isError && (
        <p className="bb-field__error" role="alert">
          {errorText(insert.error)}
        </p>
      )}
    </Modal>
  );
}

interface SaveProps {
  environmentId: string;
  channel: TemplateChannel;
  /** The composer's content as edited (saved even when the message itself is not saved yet). */
  content: TemplateContent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** «Save as template» of a composer: a new template (version 1) of the content as it is in the editor. */
export function SaveAsTemplateDialog({ environmentId, channel, content, open, onOpenChange }: SaveProps) {
  const api = useApi();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const save = useMutation({ mutationFn: () => api.createTemplate(environmentId, { channel, name: name.trim(), content, ...(description.trim() ? { description: description.trim() } : {}), notes: 'Saved from a message' }) });
  const close = (next: boolean) => {
    if (!next) {
      save.reset();
      setName('');
      setDescription('');
    }
    onOpenChange(next);
  };
  const issues = issuesOf(save.error);
  return (
    <Modal
      open={open}
      onOpenChange={close}
      title={`Save as ${TEMPLATE_CHANNEL_LABELS[channel].toLowerCase()} template`}
      footer={
        save.isSuccess ? (
          <Button variant="primary" onClick={() => close(false)}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
              Save template
            </Button>
          </>
        )
      }
    >
      {save.isSuccess ? (
        <p role="status">
          Saved as{' '}
          <Link to="/env/$environmentId/content/templates/$templateId" params={{ environmentId, templateId: save.data.id }}>
            {save.data.name}
          </Link>{' '}
          (v1).
        </p>
      ) : (
        <>
          <Field label="Template name">
            <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Description">
            <Textarea value={description} rows={2} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          {save.isError && (
            <div role="alert" className="bb-field__error">
              <p>{errorText(save.error)}</p>
              {issues.length > 0 && (
                <ul>
                  {issues.map((i) => (
                    <li key={`${i.path}-${i.code}`}>
                      {i.path ? `${i.path}: ` : ''}
                      {i.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/** «Insert template» and «Save as template» for one channel of a composer; insert only when editable, save needs `content.edit`. */
export function TemplateActions({ environmentId, channel, content, readOnly, onInsert }: { environmentId: string; channel: TemplateChannel; content: TemplateContent; readOnly: boolean; onInsert: (instance: TemplateInstance) => void }) {
  const { has } = usePermissions();
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const label = TEMPLATE_CHANNEL_LABELS[channel].toLowerCase();
  const canSave = has('content.edit') && Object.keys(content).length > 0;
  if (readOnly && !canSave) return null;
  return (
    <div className="template-actions" role="group" aria-label={`${TEMPLATE_CHANNEL_LABELS[channel]} templates`}>
      {!readOnly && (
        <Button size="sm" variant="secondary" onClick={() => setPicking(true)}>
          Insert {label} template
        </Button>
      )}
      {canSave && (
        <Button size="sm" variant="secondary" onClick={() => setSaving(true)}>
          Save as template
        </Button>
      )}
      {picking && <TemplatePickerDialog environmentId={environmentId} channel={channel} open={picking} onOpenChange={setPicking} onInsert={onInsert} />}
      {saving && <SaveAsTemplateDialog environmentId={environmentId} channel={channel} content={content} open={saving} onOpenChange={setSaving} />}
    </div>
  );
}
